import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import map from 'lodash/map';
import some from 'lodash/some';
import PropTypes from 'prop-types';

import { ContactOverlayContainer, ContactOverlayContent, ContactOverlayIncomeReq } from './ContactOverlay.styles';
import Button from 'app/shared/core/Button';
import { listingUtils_userRecentlyContactedListing } from 'app/shared/utils/listingUtils';
import Overlay from 'app/shared/modules/popups/Overlay';
import Row from 'app/shared/core/Row';
import type { ListingDetails } from 'app/types/listingDetails.type';

interface ContactOverlayProps {
  contactSubmitted: boolean;
  listing: ListingDetails;
  previousInquiryMessagePopup?: boolean;
  hasConversation?: boolean;
  overlayOffset?: string;
  satelliteEnabled?: boolean;
  shouldLockFocus?: boolean;
}

const propTypes = {
  contactSubmitted: PropTypes.bool.isRequired,
  listing: PropTypes.object.isRequired,
  previousInquiryMessagePopup: PropTypes.bool,
  hasConversation: PropTypes.bool,
  overlayOffset: PropTypes.string,
  satelliteEnabled: PropTypes.bool,
  shouldLockFocus: PropTypes.bool,
};

const ContactOverlay = (props: ContactOverlayProps) => {
  const {
    listing,
    contactSubmitted,
    previousInquiryMessagePopup,
    hasConversation,
    overlayOffset,
    satelliteEnabled,
    shouldLockFocus,
  } = props;

  const [showOverlay, setShowOverlay] = useState(false);

  // Single effect to handle all conditions for showing overlay
  useEffect(() => {
    const userOnWaitlist = listing.userOnWaitlist;
    const recentlyContacted =
      listing.userItemTypes && listing.userItemDates
        ? listingUtils_userRecentlyContactedListing(
            listing.userItemTypes as Array<never>,
            listing.userItemDates as never,
          )
        : false;
    const isStudentHousing = listing.details.studentHousing;
    const isSeniorHousing = listing.details.seniorHousing;
    const isLowIncome = listing.contact.restricted;

    const shouldShow = userOnWaitlist || recentlyContacted || isStudentHousing || isSeniorHousing || isLowIncome;

    if (shouldShow) {
      setShowOverlay(true);
    }
  }, [
    listing.userOnWaitlist,
    listing.userItemTypes,
    listing.userItemDates,
    listing.details.studentHousing,
    listing.details.seniorHousing,
    listing.contact.restricted,
    contactSubmitted,
    previousInquiryMessagePopup,
  ]);

  const handleHideOverlay = useCallback((e: React.MouseEvent | undefined) => {
    if (e) {
      e.stopPropagation();
    }

    // Add a small delay to allow FocusLock to properly clean up before unmounting
    setTimeout(() => {
      setShowOverlay(false);
    }, 0);
  }, []);

  const requiresIncomeRange = useCallback(() => {
    if (listing.contact.restricted && some(listing.contact.restrictedIncomeByOccupants)) {
      return true;
    }

    return false;
  }, [listing.contact.restricted, listing.contact.restrictedIncomeByOccupants]);

  const getOverlayMessage = useCallback(() => {
    const userOnWaitlist = listing.userOnWaitlist;
    const recentlyContacted =
      listing.userItemTypes && listing.userItemDates
        ? listingUtils_userRecentlyContactedListing(
            listing.userItemTypes as Array<never>,
            listing.userItemDates as never,
          )
        : false;
    const isStudentHousing = listing.details.studentHousing;
    const isSeniorHousing = listing.details.seniorHousing;
    const isLowIncome = listing.contact.restricted;

    if (userOnWaitlist) {
      return (
        <ContactOverlayContent>
          <Row>Your name has already been added to the waitlist.</Row>
          <Row>
            <Button size="lg" btnType="text-color" onClick={handleHideOverlay}>
              Continue
            </Button>
          </Row>
        </ContactOverlayContent>
      );
    } else if (recentlyContacted) {
      return (
        <ContactOverlayContent>
          <Row>You have already contacted this property.</Row>
          <Row>
            <Button size="lg" btnType="text-color" onClick={handleHideOverlay}>
              Contact again?
            </Button>
          </Row>
        </ContactOverlayContent>
      );
    } else if (isStudentHousing) {
      return (
        <ContactOverlayContent>
          <Row>This property is student-only housing. You must qualify to live here.</Row>
          <Row>
            <Button size="lg" btnType="text-color" onClick={handleHideOverlay}>
              Continue
            </Button>
          </Row>
        </ContactOverlayContent>
      );
    } else if (isSeniorHousing) {
      return (
        <ContactOverlayContent>
          <Row>
            You are about to contact a senior housing community. Verification of age requirements is required to live
            here.
          </Row>
          <Row>
            <Button size="lg" btnType="text-color" onClick={handleHideOverlay}>
              Continue
            </Button>
          </Row>
        </ContactOverlayContent>
      );
    } else if (isLowIncome) {
      return (
        <ContactOverlayContent>
          <Row>{listing.contact.message}</Row>
          {requiresIncomeRange() && (
            <Row>
              {map(listing.contact.restrictedIncomeByOccupants, (restriction, persons) => {
                if (restriction === '') {
                  return null;
                }

                return (
                  <ContactOverlayIncomeReq key={`${restriction}${persons}`}>
                    {Number(persons) > 1 ? `${persons} residents` : `${persons} resident`}, income below {restriction}
                  </ContactOverlayIncomeReq>
                );
              })}
            </Row>
          )}
          <Row>
            <Button size="lg" btnType="text-color" onClick={handleHideOverlay}>
              Yes, I qualify!
            </Button>
          </Row>
        </ContactOverlayContent>
      );
    }
  }, [
    listing.userOnWaitlist,
    listing.userItemTypes,
    listing.userItemDates,
    listing.details.studentHousing,
    listing.details.seniorHousing,
    listing.contact.restricted,
    listing.contact.message,
    listing.contact.restrictedIncomeByOccupants,
    handleHideOverlay,
    requiresIncomeRange,
  ]);

  if (!showOverlay || (hasConversation && satelliteEnabled)) {
    return null;
  }

  return (
    <Overlay onHidePopup={handleHideOverlay} overlayOffset={overlayOffset} shouldLockFocus={shouldLockFocus}>
      <ContactOverlayContainer>{getOverlayMessage()}</ContactOverlayContainer>
    </Overlay>
  );
};

ContactOverlay.propTypes = propTypes;
export default connect()(ContactOverlay);
